import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText, FileSpreadsheet, BarChart3, Calendar, TrendingUp, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";

export default function ReportsPage({ companyId, company }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [summary, setSummary] = useState(null);
  const [downloadingPL, setDownloadingPL] = useState(false);
  const [downloadingAnnual, setDownloadingAnnual] = useState(false);
  const [downloadingQuarterly, setDownloadingQuarterly] = useState(false);
  
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear.toString());
  const [selectedQuarter, setSelectedQuarter] = useState("1");
  
  const years = Array.from({ length: 5 }, (_, i) => (currentYear - i).toString());

  useEffect(() => {
    if (!companyId) return;
    Promise.all([
      api.get("/schedule-c-categories"),
      api.get(`/companies/${companyId}/dashboard`)
    ]).then(([catRes, dashRes]) => {
      setCategories(catRes.data);
      setSummary(dashRes.data);
    }).finally(() => setLoading(false));
  }, [companyId]);

  const downloadCSV = () => {
    const token = localStorage.getItem("auth_token");
    let url = `${BACKEND_URL}/api/companies/${companyId}/reports/csv`;
    const params = new URLSearchParams();
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);
    if (params.toString()) url += `?${params.toString()}`;
    
    fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' })
      .then(res => res.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${company?.name || 'report'}_transactions.csv`;
        a.click();
        toast.success("CSV downloaded - includes source file references");
      })
      .catch(() => toast.error("Download failed"));
  };

  const downloadPDF = () => {
    const token = localStorage.getItem("auth_token");
    let url = `${BACKEND_URL}/api/companies/${companyId}/reports/pdf`;
    const params = new URLSearchParams();
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);
    if (params.toString()) url += `?${params.toString()}`;
    
    fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' })
      .then(res => res.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${company?.name || 'report'}_schedule_c.pdf`;
        a.click();
        toast.success("Schedule C PDF downloaded");
      })
      .catch(() => toast.error("Download failed"));
  };

  const downloadPLPdf = () => {
    setDownloadingPL(true);
    const token = localStorage.getItem("auth_token");
    let url = `${BACKEND_URL}/api/companies/${companyId}/reports/pl-pdf`;
    const params = new URLSearchParams();
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);
    if (params.toString()) url += `?${params.toString()}`;
    
    fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' })
      .then(res => { if (!res.ok) throw new Error(); return res.blob(); })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${company?.name || 'report'}_PL_Report.pdf`;
        a.click();
        toast.success("P/L Report downloaded");
      })
      .catch(() => toast.error("Download failed"))
      .finally(() => setDownloadingPL(false));
  };

  const downloadAnnualReport = () => {
    setDownloadingAnnual(true);
    const token = localStorage.getItem("auth_token");
    const url = `${BACKEND_URL}/api/companies/${companyId}/reports/annual-pdf?year=${selectedYear}`;
    
    fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' })
      .then(res => { if (!res.ok) throw new Error(); return res.blob(); })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${company?.name || 'report'}_Annual_Report_${selectedYear}.pdf`;
        a.click();
        toast.success(`Annual Report for ${selectedYear} downloaded`);
      })
      .catch(() => toast.error("Download failed"))
      .finally(() => setDownloadingAnnual(false));
  };

  const downloadQuarterlyReport = () => {
    setDownloadingQuarterly(true);
    const token = localStorage.getItem("auth_token");
    const url = `${BACKEND_URL}/api/companies/${companyId}/reports/quarterly-pdf?year=${selectedYear}&quarter=${selectedQuarter}`;
    
    fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' })
      .then(res => { if (!res.ok) throw new Error(); return res.blob(); })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${company?.name || 'report'}_Q${selectedQuarter}_${selectedYear}_Report.pdf`;
        a.click();
        toast.success(`Q${selectedQuarter} ${selectedYear} Report downloaded`);
      })
      .catch(() => toast.error("Download failed"))
      .finally(() => setDownloadingQuarterly(false));
  };

  const incomeCategories = categories.filter(c => c.type === "income");
  const expenseCategories = categories.filter(c => c.type === "expense");

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="reports-page">
      <div>
        <h1 className="text-3xl font-heading font-bold text-white">Reports</h1>
        <p className="text-muted-foreground mt-1">Generate financial reports with audit trail</p>
      </div>

      <Card className="border-border/50" style={{ background: 'hsl(233 30% 8%)' }}>
        <CardHeader>
          <CardTitle className="text-base font-heading text-white flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Custom Date Range Reports
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <Label className="text-muted-foreground text-sm">Start Date</Label>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                className="mt-1.5 bg-secondary border-border text-white w-[180px]" data-testid="report-start-date" />
            </div>
            <div>
              <Label className="text-muted-foreground text-sm">End Date</Label>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                className="mt-1.5 bg-secondary border-border text-white w-[180px]" data-testid="report-end-date" />
            </div>
            <Button onClick={downloadCSV} variant="outline" className="border-border text-white" data-testid="download-csv-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Export CSV
            </Button>
            <Button onClick={downloadPDF} variant="outline" className="border-border text-white" data-testid="download-pdf-btn">
              <FileText className="w-4 h-4 mr-2" /> Schedule C
            </Button>
            <Button onClick={downloadPLPdf} disabled={downloadingPL} data-testid="download-pl-pdf-btn">
              {downloadingPL ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <BarChart3 className="w-4 h-4 mr-2" />}
              P/L Report
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-3">CSV includes Source File and Upload Date for audit.</p>
        </CardContent>
      </Card>

      <Card className="border-border/50" style={{ background: 'hsl(233 30% 8%)' }}>
        <CardHeader>
          <CardTitle className="text-base font-heading text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Annual & Quarterly Reports
          </CardTitle>
          <CardDescription className="text-muted-foreground">Comprehensive reports for tax filing</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 rounded-lg bg-secondary/50 border border-border/50">
              <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-amber-500" />
                Annual Report
              </h3>
              <p className="text-xs text-muted-foreground mb-3">Full year with quarterly breakdown and audit trail.</p>
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <Label className="text-muted-foreground text-xs">Year</Label>
                  <Select value={selectedYear} onValueChange={setSelectedYear}>
                    <SelectTrigger className="mt-1 bg-secondary border-border text-white" data-testid="annual-year-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {years.map(y => <SelectItem key={y} value={y} className="text-white">{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={downloadAnnualReport} disabled={downloadingAnnual} className="bg-amber-600 hover:bg-amber-700" data-testid="download-annual-btn">
                  {downloadingAnnual ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : `Generate ${selectedYear}`}
                </Button>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-secondary/50 border border-border/50">
              <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-cyan-500" />
                Quarterly Report
              </h3>
              <p className="text-xs text-muted-foreground mb-3">Monthly breakdown with expense categorization.</p>
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <Label className="text-muted-foreground text-xs">Year</Label>
                  <Select value={selectedYear} onValueChange={setSelectedYear}>
                    <SelectTrigger className="mt-1 bg-secondary border-border text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {years.map(y => <SelectItem key={y} value={y} className="text-white">{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label className="text-muted-foreground text-xs">Quarter</Label>
                  <Select value={selectedQuarter} onValueChange={setSelectedQuarter}>
                    <SelectTrigger className="mt-1 bg-secondary border-border text-white" data-testid="quarter-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="1" className="text-white">Q1 (Jan-Mar)</SelectItem>
                      <SelectItem value="2" className="text-white">Q2 (Apr-Jun)</SelectItem>
                      <SelectItem value="3" className="text-white">Q3 (Jul-Sep)</SelectItem>
                      <SelectItem value="4" className="text-white">Q4 (Oct-Dec)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={downloadQuarterlyReport} disabled={downloadingQuarterly} className="bg-cyan-600 hover:bg-cyan-700" data-testid="download-quarterly-btn">
                  {downloadingQuarterly ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : `Q${selectedQuarter}`}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {summary && (
        <Card className="border-border/50" style={{ background: 'hsl(233 30% 8%)' }}>
          <CardHeader><CardTitle className="text-base font-heading text-white">Financial Summary</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-lg bg-secondary">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Income</p>
                <p className="text-xl font-bold font-mono text-profit mt-1">${summary.summary.total_income.toLocaleString()}</p>
              </div>
              <div className="p-4 rounded-lg bg-secondary">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Expenses</p>
                <p className="text-xl font-bold font-mono text-loss mt-1">${summary.summary.total_expenses.toLocaleString()}</p>
              </div>
              <div className="p-4 rounded-lg bg-secondary">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Net Profit/Loss</p>
                <p className={`text-xl font-bold font-mono mt-1 ${summary.summary.net_profit >= 0 ? 'text-profit' : 'text-loss'}`}>
                  ${summary.summary.net_profit.toLocaleString()}
                </p>
              </div>
              <div className="p-4 rounded-lg bg-secondary">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Transactions</p>
                <p className="text-xl font-bold font-mono text-white mt-1">{summary.summary.total_transactions}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-border/50" style={{ background: 'hsl(233 30% 8%)' }}>
          <CardHeader><CardTitle className="text-base font-heading text-white">Schedule C - Income</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-border/50 hover:bg-transparent">
                  <TableHead className="text-muted-foreground text-xs uppercase">Line</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase">Category</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {incomeCategories.map(c => (
                  <TableRow key={c.code} className="border-border/30">
                    <TableCell className="text-sm font-mono text-muted-foreground">{c.line_number}</TableCell>
                    <TableCell className="text-sm text-white">{c.name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="border-border/50" style={{ background: 'hsl(233 30% 8%)' }}>
          <CardHeader><CardTitle className="text-base font-heading text-white">Schedule C - Expenses</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[500px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50 hover:bg-transparent">
                    <TableHead className="text-muted-foreground text-xs uppercase">Line</TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase">Category</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {expenseCategories.map(c => (
                    <TableRow key={c.code} className="border-border/30">
                      <TableCell className="text-sm font-mono text-muted-foreground">{c.line_number}</TableCell>
                      <TableCell className="text-sm text-white">{c.name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {summary?.category_breakdown?.length > 0 && (
        <Card className="border-border/50" style={{ background: 'hsl(233 30% 8%)' }}>
          <CardHeader><CardTitle className="text-base font-heading text-white">Expense Breakdown</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-border/50 hover:bg-transparent">
                  <TableHead className="text-muted-foreground text-xs uppercase">Category</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase text-right">Amount</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase text-right">%</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.category_breakdown.map((c, i) => (
                  <TableRow key={i} className="border-border/30">
                    <TableCell className="text-sm text-white">{c.name}</TableCell>
                    <TableCell className="text-sm font-mono text-loss text-right">${c.value.toLocaleString()}</TableCell>
                    <TableCell className="text-sm font-mono text-muted-foreground text-right">
                      {summary.summary.total_expenses > 0 ? `${((c.value / summary.summary.total_expenses) * 100).toFixed(1)}%` : '0%'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
